/* eslint-disable css-modules/no-unused-class */
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useMemo,
  type PropsWithChildren,
  type MutableRefObject,
  type HTMLAttributes,
} from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import cn from 'classnames';

import styles from './EnhancedScrollableArea.module.css';
import { ScrollableArea } from './ScrollableArea';
import type { ScrollableAreaProps } from './types';

interface EnhancedScrollableAreaContextType {
  scrollElementRef: MutableRefObject<HTMLDivElement | null> | null;
  updateScrollAlignment: () => void;
  isInitialized: boolean;
}

// Create a context for items size
const EnhancedScrollableAreaContext =
  createContext<EnhancedScrollableAreaContextType>({
    scrollElementRef: null,
    updateScrollAlignment: () => {},
    isInitialized: false,
  });

export interface EnhancedScrollableArrowProps {
  /**
   * The tab index of the arrow button
   */
  tabIndex?: number;
  /**
   * The position of the arrow button
   */
  position: 'left' | 'right';
  /**
   * The onClick handler of the arrow button
   */
  onClick?: (scrollContainer: HTMLDivElement) => void;
  /**
   * The className of the arrow button
   */
  className?: string;
  /**
   * The asChild prop of the arrow button
   */
  asChild?: boolean;
  /**
   * The children of the arrow button
   */
  children: React.ReactElement;
}

/**
 * EnhancedScrollableArrow component with ability to show scroll arrows and scroll the content by clicking on the arrow
 */
export const EnhancedScrollableArrow: React.FC<
  EnhancedScrollableArrowProps
> = ({ position, children, tabIndex = -1, onClick, className, asChild }) => {
  const { scrollElementRef } = useContext(EnhancedScrollableAreaContext);

  const props: HTMLAttributes<HTMLButtonElement> = {
    className: cn(
      styles['scroll-arrow'],
      styles[`scroll-arrow-${position}`],
      className
    ),
    tabIndex,
    onClick: () => {
      const scrollContainer = scrollElementRef?.current;
      if (scrollContainer) {
        onClick?.(scrollContainer);
      }
    },
  };

  if (asChild) {
    const child = React.Children.only(children);
    return React.cloneElement(child, props);
  }

  return <button {...props}>{children}</button>;
};

export type EnhancedScrollableViewportProps = ScrollableAreaProps;

/**
 * EnhancedScrollableViewport component with a wrapper around the ScrollableArea component
 */
export const EnhancedScrollableViewport: React.FC<
  EnhancedScrollableViewportProps
> = (props) => {
  const { scrollElementRef, updateScrollAlignment } = useContext(
    EnhancedScrollableAreaContext
  );

  return (
    <ScrollableArea
      {...props}
      ref={(node) => {
        if (node && scrollElementRef) {
          scrollElementRef.current = node;
          updateScrollAlignment();
        }
      }}
      onScroll={(e) => {
        updateScrollAlignment();
        props.onScroll?.(e);
      }}
    />
  );
};

type ScrollAlignment = 'left' | 'right' | 'center' | 'none' | 'indeterminate';

const SCROLL_ARROW_TOLERANCE = 10;

const getScrollAlignment = (
  scrollElement?: HTMLDivElement | null
): ScrollAlignment => {
  if (!scrollElement) {
    return 'none';
  }

  const { scrollLeft, scrollWidth, clientWidth } = scrollElement;
  if (clientWidth === scrollWidth) {
    return 'none';
  }

  if (scrollLeft < SCROLL_ARROW_TOLERANCE) {
    return 'left';
  }

  if (scrollLeft + clientWidth > scrollWidth - SCROLL_ARROW_TOLERANCE) {
    return 'right';
  }

  return 'center';
};

export interface EnhancedScrollableVirtualProps {
  /**
   * A function that returns the size of an item
   * @note A potential performance bottleneck. Please memoize this function
   */
  estimateSize: (index: number) => number;
  /**
   * The direction of virtualization - vertical (default) or horizontal
   */
  direction?: 'vertical' | 'horizontal';
  /**
   * Optional class name for the container
   */
  className?: string;
  /**
   * Optional function that extracts item key for more stable rendering
   * @note A potential performance bottleneck. Please memoize this function
   */
  getItemKey?: (index: number) => string | number;
  /**
   * Overscan - number of items to render outside of the visible area
   * @default 5
   */
  overscan?: number;
  /**
   * The children elements to be virtualized
   */
  children: React.ReactNode[];
}

/**
 * Virtual component for EnhancedScrollableArea
 * Renders only the items that are visible in the viewport for performance
 */
export const EnhancedScrollableVirtual: React.FC<
  EnhancedScrollableVirtualProps
> = ({
  children,
  estimateSize,
  direction = 'vertical',
  className,
  getItemKey,
  overscan = 5,
}): ReturnType<React.FC> => {
  const { scrollElementRef, isInitialized } = useContext(
    EnhancedScrollableAreaContext
  );

  const virtualizer = useVirtualizer({
    count: children.length,
    // Only use scrollElementRef when it's ready
    getScrollElement: () =>
      isInitialized ? scrollElementRef?.current || null : null,
    estimateSize,
    getItemKey,
    overscan,
    horizontal: direction === 'horizontal',
  });

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  const containerStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'relative',
      height: direction === 'vertical' ? `${totalSize}px` : '100%',
      width: direction === 'horizontal' ? `${totalSize}px` : '100%',
    }),
    [direction, totalSize]
  );

  // Hide it if not ready
  if (!isInitialized) {
    return null;
  }

  return (
    <div
      className={cn(styles['virtual-container'], className)}
      style={containerStyle}
    >
      {virtualItems.map((virtualItem) => {
        const child = children[virtualItem.index];

        const itemStyle: React.CSSProperties = {
          position: 'absolute',
          [direction === 'vertical' ? 'top' : 'left']: `${virtualItem.start}px`,
          [direction === 'vertical' ? 'height' : 'width']:
            `${virtualItem.size}px`,
          width: direction === 'vertical' ? '100%' : undefined,
          height: direction === 'horizontal' ? '100%' : undefined,
        };

        return (
          <div
            key={virtualItem.key}
            ref={virtualizer.measureElement}
            className={styles['virtual-item']}
            data-index={virtualItem.index}
            data-virtual-index={virtualItem.index}
            style={itemStyle}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

type ScrollableAreaComponent = React.FC<
  PropsWithChildren<HTMLAttributes<HTMLDivElement>>
> & {
  Viewport: React.FC<ScrollableAreaProps>;
  Arrow: React.FC<EnhancedScrollableArrowProps>;
  Virtual: typeof EnhancedScrollableVirtual;
};

/**
 * EnhancedScrollableArea component with ability to show scroll arrows
 * This component is useful for creating a scrollable area with the controls
 * Currently it only supports horizontal scrolling arrows but can be extended to support vertical arrows,
 * sticky headers, etc.
 * The component itself doesn't have any particular props (it accepts `HTMLAttributes` of the `div` element)
 * For more information about configuration see the `Overview` section
 */
export const EnhancedScrollableArea: ScrollableAreaComponent = ({
  children,
  className,
  ...props
}) => {
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const [scrollPosition, setScrollAlignment] =
    useState<ScrollAlignment>('indeterminate');

  const isInitialized = scrollPosition !== 'indeterminate';

  const contextValue = useMemo(
    () => ({
      scrollElementRef: scrollableRef,
      updateScrollAlignment: () => {
        const scrollPosition = getScrollAlignment(scrollableRef.current);
        setScrollAlignment(scrollPosition);
      },
      isInitialized,
    }),
    [setScrollAlignment, isInitialized]
  );

  return (
    <EnhancedScrollableAreaContext.Provider value={contextValue}>
      <div
        className={cn(styles.root, className)}
        data-scroll-position={scrollPosition}
        {...props}
      >
        {children}
      </div>
    </EnhancedScrollableAreaContext.Provider>
  );
};

EnhancedScrollableArea.Viewport = EnhancedScrollableViewport;
EnhancedScrollableArea.Arrow = EnhancedScrollableArrow;
EnhancedScrollableArea.Virtual = EnhancedScrollableVirtual;
